import React from 'react';
import {
  SkeletonAvatar,
  SkeletonBlock,
  SkeletonButton,
  SkeletonLine,
  SkeletonMedia,
  SkeletonPill,
} from './Skeleton';

type DetailMedia = 'poster' | 'avatar' | 'wide' | 'block' | 'none';

type DetailPageSkeletonProps = {
  variant?: 'artist' | 'event' | 'venue' | 'default';
  /**
   * Hero media shape. When omitted it is derived from `variant`. Pass `'none'`
   * for pages whose real header is text-only (e.g. DetailHeader) so the skeleton
   * does not show a poster/avatar that collapses away on load.
   */
  media?: DetailMedia;
  /** Section grid columns. Use 1 for single-column card stacks. Default 2. */
  columns?: 1 | 2;
  /** Show the back-pill + action-button toolbar row. Default true. */
  showToolbar?: boolean;
  /** Override the container max-width (number = px). Default 1200. */
  maxWidth?: number | string;
  sectionCount?: number;
  actionCount?: number;
  className?: string;
};

const normalizeCount = (count: unknown, fallback = 3) => Math.max(1, Number(count) || fallback);

const toCssLength = (value: number | string | undefined) => {
  if (value == null || value === '') return undefined;
  return typeof value === 'number' ? `${value}px` : value;
};

const mediaByVariant: Record<NonNullable<DetailPageSkeletonProps['variant']>, DetailMedia> = {
  artist: 'avatar',
  event: 'poster',
  venue: 'wide',
  default: 'block',
};

const renderMedia = (media: DetailMedia): React.ReactNode => {
  switch (media) {
    case 'none':
      return null;
    case 'avatar':
      return <SkeletonAvatar className="detail-skeleton__avatar" />;
    case 'poster':
      return <SkeletonMedia className="detail-skeleton__media" />;
    case 'wide':
      return <SkeletonMedia className="detail-skeleton__media detail-skeleton__media--wide" />;
    case 'block':
    default:
      return <SkeletonBlock className="detail-skeleton__media" />;
  }
};

export default function DetailPageSkeleton({
  variant = 'default',
  media,
  columns = 2,
  showToolbar = true,
  maxWidth,
  sectionCount = 3,
  actionCount = 2,
  className = '',
}: DetailPageSkeletonProps) {
  const sections = Array.from({ length: normalizeCount(sectionCount) }, (_, index) => index);
  const actions = Array.from({ length: normalizeCount(actionCount, 2) }, (_, index) => index);
  const effectiveMedia: DetailMedia = media || mediaByVariant[variant] || 'block';
  const mediaNode = renderMedia(effectiveMedia);
  const cssMaxWidth = toCssLength(maxWidth);

  const classes = [
    'detail-skeleton',
    `detail-skeleton--${variant}`,
    columns === 1 ? 'detail-skeleton--cols-1' : '',
    mediaNode ? '' : 'detail-skeleton--no-media',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      role="status"
      aria-live="polite"
      style={cssMaxWidth ? ({ ['--detail-skeleton-max-width' as string]: cssMaxWidth } as React.CSSProperties) : undefined}
    >
      {showToolbar && (
        <div className="detail-skeleton__toolbar">
          <SkeletonPill className="detail-skeleton__back" />
          <div className="detail-skeleton__actions">
            {actions.map((index) => (
              <SkeletonButton key={`detail-skeleton-action-${index}`} className="detail-skeleton__action" />
            ))}
          </div>
        </div>
      )}

      <section className="detail-skeleton__hero" aria-hidden="true">
        {mediaNode}
        <div className="detail-skeleton__summary">
          <SkeletonLine className="detail-skeleton__eyebrow" />
          <SkeletonLine className="detail-skeleton__title" />
          <SkeletonLine className="detail-skeleton__subtitle" />
          <div className="detail-skeleton__chips">
            <SkeletonPill className="detail-skeleton__chip" />
            <SkeletonPill className="detail-skeleton__chip detail-skeleton__chip--short" />
            <SkeletonPill className="detail-skeleton__chip detail-skeleton__chip--medium" />
          </div>
        </div>
      </section>

      <div className="detail-skeleton__sections" aria-hidden="true">
        {sections.map((index) => (
          <section key={`detail-skeleton-section-${index}`} className="detail-skeleton__section">
            <div className="detail-skeleton__sectionHead">
              <SkeletonLine className="detail-skeleton__sectionTitle" />
              <SkeletonPill className="detail-skeleton__sectionPill" />
            </div>
            <SkeletonLine className="detail-skeleton__line detail-skeleton__line--wide" />
            <SkeletonLine className="detail-skeleton__line" />
            <SkeletonLine className="detail-skeleton__line detail-skeleton__line--short" />
          </section>
        ))}
      </div>

      <span className="sr-only">詳細情報を読み込み中...</span>
    </div>
  );
}
